import type {
	Rdo as ApiRdo,
	RdoActivity as ApiRdoActivity,
	RdoPerformance as ApiRdoPerformance,
	RdoPhoto as ApiRdoPhoto,
	RdoScheduledService as ApiRdoScheduledService,
	RdoSummary as ApiRdoSummary,
	RdoPhotoSize,
	StageStatus,
} from "@/adapters/api";
import type { WeatherCondition } from "@/features/feed/types.ts";
import { WEATHER_META } from "@/features/feed/utils/weather.ts";
import { STAGE_STATUS } from "@/features/stages/utils/status.ts";
import type {
	Rdo,
	RdoActivity,
	RdoPerformance,
	RdoPhoto,
	RdoScheduledService,
	RdoSummary,
} from "../types.ts";
import { isRdoPhotoSize } from "./photo-size.ts";

function toWeather(value: string | null): WeatherCondition | undefined {
	return value && value in WEATHER_META
		? (value as WeatherCondition)
		: undefined;
}

function toStageStatus(value: string | undefined): StageStatus | undefined {
	return value && value in STAGE_STATUS ? (value as StageStatus) : undefined;
}

export function toRdoPhoto(photo: ApiRdoPhoto): RdoPhoto {
	return {
		id: photo.id,
		position: photo.position,
		sourcePostMediaId: photo.source_post_media_id || undefined,
		url: photo.url,
		width: photo.width ?? undefined,
		height: photo.height ?? undefined,
		caption: photo.caption ?? "",
	};
}

export function toRdoActivity(activity: ApiRdoActivity): RdoActivity {
	return {
		id: activity.id,
		position: activity.position,
		title: activity.title,
		stageId: activity.stage_id || undefined,
		status: toStageStatus(activity.status),
		occurredAt: activity.occurred_at,
		description: activity.description ?? "",
		sourcePostId: activity.source_post_id || undefined,
		photos: (activity.photos ?? []).map(toRdoPhoto),
	};
}

export function toRdoScheduledService(
	service: ApiRdoScheduledService,
): RdoScheduledService {
	return {
		id: service.id,
		position: service.position,
		description: service.description ?? "",
		stageId: service.stage_id || undefined,
	};
}

function toPhotoSize(value: string | undefined): RdoPhotoSize {
	return value && isRdoPhotoSize(value) ? value : "small";
}

function toPerformance(
	performance: ApiRdoPerformance | undefined,
): RdoPerformance | undefined {
	if (!performance) return undefined;
	return {
		planned: performance.planned,
		actual: performance.actual,
		deviation: performance.deviation,
		completedCount: performance.completed_count,
		interval: performance.interval,
		noBaseline: performance.no_baseline,
	};
}

export function toRdoSummary(rdo: ApiRdoSummary): RdoSummary {
	return {
		id: rdo.id,
		sequenceNumber: rdo.sequence_number,
		reportDate: rdo.report_date,
		revision: rdo.revision,
		status: rdo.status,
		technicalManager: rdo.technical_manager ?? "",
		weatherCondition: toWeather(rdo.weather_condition),
		firstRecordAt: rdo.first_record_at ?? undefined,
		lastRecordAt: rdo.last_record_at ?? undefined,
		inProgressCount: rdo.in_progress_count,
		completedCount: rdo.completed_count,
		observations: rdo.observations ?? "",
		signatures: {
			diary: rdo.signatures?.diary ?? "",
			technical: rdo.signatures?.technical ?? "",
			contractor: rdo.signatures?.contractor ?? "",
		},
		crewAnnexEnabled: rdo.crew_annex_enabled,
		performanceEnabled: rdo.performance_enabled ?? true,
		photoSize: toPhotoSize(rdo.photo_size),
		performance: toPerformance(rdo.performance),
		hasPdf: !!rdo.pdf_url,
		failureReason: rdo.failure_reason || undefined,
		revisedFromId: rdo.revised_from_id || undefined,
		supersededById: rdo.superseded_by_id || undefined,
		finalizedAt: rdo.finalized_at ?? undefined,
		createdAt: rdo.created_at,
		updatedAt: rdo.updated_at,
	};
}

export function toRdo(rdo: ApiRdo): Rdo {
	return {
		...toRdoSummary(rdo),
		activities: (rdo.activities ?? []).map(toRdoActivity),
		scheduledServices: (rdo.scheduled_services ?? []).map(
			toRdoScheduledService,
		),
	};
}
